import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { EndpointFactory } from './endpoint-factory.service';
import { ConfigurationService } from './configuration.service';
import { Roles } from '../models/enums';


@Injectable({ providedIn: 'root'})
export class AccountEndpoint extends EndpointFactory {

  private readonly _usersUrl: string = '/api/account/users';
  private readonly _userByUserNameUrl: string = '/api/account/users/username';
  private readonly _currentUserUrl: string = '/api/account/users/me';
  private readonly _currentUserPreferencesUrl: string = '/api/account/users/me/preferences';
  private readonly _unblockUserUrl: string = '/api/account/users/unblock';
  private readonly _rolesUrl: string = '/api/account/roles';
  private readonly _roleByRoleNameUrl: string = '/api/account/roles/name';
  private readonly _permissionsUrl: string = '/api/account/permissions';
  private readonly _resetUserPasswordUrl: string = '/api/account/resetuserpassword';
  private readonly _loginConfigurationUrl: string = '/api/account/loginconfig';

  get usersUrl() { return this.configurations.baseUrl + this._usersUrl; }
  get userByUserNameUrl() { return this.configurations.baseUrl + this._userByUserNameUrl; }
  get currentUserUrl() { return this.configurations.baseUrl + this._currentUserUrl; }
  get currentUserPreferencesUrl() { return this.configurations.baseUrl + this._currentUserPreferencesUrl; }
  get unblockUserUrl() { return this.configurations.baseUrl + this._unblockUserUrl; }
  get rolesUrl() { return this.configurations.baseUrl + this._rolesUrl; }
  get roleByRoleNameUrl() { return this.configurations.baseUrl + this._roleByRoleNameUrl; }
  get permissionsUrl() { return this.configurations.baseUrl + this._permissionsUrl; }
  get resetUserPasswordUrl() { return this.configurations.baseUrl + this._resetUserPasswordUrl; }
  get loginConfigurationUrl() { return this.configurations.baseUrl + this._loginConfigurationUrl; }

  constructor(http: HttpClient, configurations: ConfigurationService, injector: Injector) {
    super(http, configurations, injector);
  }

  getUserSelfEndpoint<T>(): Observable<T> {
    return this.http.get<T>(this.currentUserUrl, this.getRequestHeaders());
  }


  getUserEndpoint<T>(userId?: string): Observable<T> {
    const endpointUrl = userId ? `${this.usersUrl}/${userId}` : this.currentUserUrl;
    return this.http.get<T>(endpointUrl, this.getRequestHeaders());
  }


  getUserByUserNameEndpoint<T>(userName: string): Observable<T> {
    const endpointUrl = `${this.userByUserNameUrl}/${userName}`;
    return this.http.get<T>(endpointUrl, this.getRequestHeaders());
  }


  getUsersEndpoint<T>(page?: number, pageSize?: number): Observable<T> {
    const endpointUrl = page && pageSize ? `${this.usersUrl}/${page}/${pageSize}` : this.usersUrl;
    return this.http.get<T>(endpointUrl, this.getRequestHeaders());
  }

  getResetUserPasswordEndpoint<T>(userEmail: string): Observable<T> {
    return this.http.post<T>(this.resetUserPasswordUrl, JSON.stringify(userEmail), this.getRequestHeaders());
  }

  getNewUserEndpoint<T>(userObject: any): Observable<T> {
    return this.http.post<T>(this.usersUrl, JSON.stringify(userObject), this.getRequestHeaders());
  }

  getUpdateUserEndpoint<T>(userObject: any, activeRole: Roles, userId?: string): Observable<T> {
    const endpointUrl = userId ? `${this.usersUrl}/${userId}` : this.currentUserUrl;
    const params = new HttpParams().set('activeRole', activeRole.toString());
    const options = {...this.getRequestHeaders(), params: params};

    return this.http.put<T>(endpointUrl, JSON.stringify(userObject), options);
  }

  getPatchUpdateUserEndpoint<T>(patch: {}, userId?: string): Observable<T>
  getPatchUpdateUserEndpoint<T>(value: any, op: string, path: string, from?: any, userId?: string): Observable<T>
  getPatchUpdateUserEndpoint<T>(valueOrPatch: any, opOrUserId?: string, path?: string, from?: any, userId?: string): Observable<T> {
    let endpointUrl: string;
    let patchDocument: {};

    if (path) {
      endpointUrl = userId ? `${this.usersUrl}/${userId}` : this.currentUserUrl;
      patchDocument = from ?
        [{ "value": valueOrPatch, "path": path, "op": opOrUserId, "from": from }] :
        [{ "value": valueOrPatch, "path": path, "op": opOrUserId }];
    }
    else {
      endpointUrl = opOrUserId ? `${this.usersUrl}/${opOrUserId}` : this.currentUserUrl;
      patchDocument = valueOrPatch;
    }

    return this.http.patch<T>(endpointUrl, JSON.stringify(patchDocument), this.getRequestHeaders());
  }


  getUserPreferencesEndpoint<T>(): Observable<T> {
    return this.http.get<T>(this.currentUserPreferencesUrl, this.getRequestHeaders());
  }

  getUpdateUserPreferencesEndpoint<T>(configuration: string): Observable<T> {
    return this.http.put<T>(this.currentUserPreferencesUrl, JSON.stringify(configuration), this.getRequestHeaders());
  }

  getUnblockUserEndpoint<T>(userId: string): Observable<T> {
    const endpointUrl = `${this.unblockUserUrl}/${userId}`;
    return this.http.put<T>(endpointUrl, null, this.getRequestHeaders());
  }

  getDeleteUserEndpoint<T>(userId: string): Observable<T> {
    const endpointUrl = `${this.usersUrl}/${userId}`;
    return this.http.delete<T>(endpointUrl, this.getRequestHeaders());
  }

  getRoleEndpoint<T>(roleId: string): Observable<T> {
    const endpointUrl = `${this.rolesUrl}/${roleId}`;
    return this.http.get<T>(endpointUrl, this.getRequestHeaders());
  }

  getRoleByRoleNameEndpoint<T>(roleName: string): Observable<T> {
    const endpointUrl = `${this.roleByRoleNameUrl}/${roleName}`;
    return this.http.get<T>(endpointUrl, this.getRequestHeaders());
  }

  getRolesEndpoint<T>(page?: number, pageSize?: number): Observable<T> {
    const endpointUrl = page && pageSize ? `${this.rolesUrl}/${page}/${pageSize}` : this.rolesUrl;
    return this.http.get<T>(endpointUrl, this.getRequestHeaders());
  }

  getNewRoleEndpoint<T>(roleObject: any): Observable<T> {
    return this.http.post<T>(this.rolesUrl, JSON.stringify(roleObject), this.getRequestHeaders());
  }

  getUpdateRoleEndpoint<T>(roleObject: any, roleId: string): Observable<T> {
    const endpointUrl = `${this.rolesUrl}/${roleId}`;
    return this.http.put<T>(endpointUrl, JSON.stringify(roleObject), this.getRequestHeaders());
  }

  getDeleteRoleEndpoint<T>(roleId: string): Observable<T> {
    const endpointUrl = `${this.rolesUrl}/${roleId}`;
    return this.http.delete<T>(endpointUrl, this.getRequestHeaders());
  }

  getPermissionsEndpoint<T>(): Observable<T> {
    return this.http.get<T>(this.permissionsUrl, this.getRequestHeaders());
  }

  getWestatCodersEndpoint<T>(coderteams: number[]): Observable<T> {
    var endpointUrl = `${this.usersUrl}/westat`;
    const options = { ...this.getRequestHeaders() };

    return this.http.post<T>(endpointUrl, coderteams, options);
  }

  getFullWestatCodersEndpoint<T>(): Observable<T> {
    var endpointUrl = `${this.usersUrl}/fullwestat`;
    const options = { ...this.getRequestHeaders() };

    return this.http.post<T>(endpointUrl, options);
  }

  getLoginConfigurationEndpoint<T>(): Observable<T> {
    var endpointUrl = `${this.loginConfigurationUrl}`;
    const options = { ...this.getRequestHeaders() };

    return this.http.get<T>(endpointUrl, options);
  }
}
